import React, { useMemo } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Controls, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Clock, UserPlus, Users, Bell, GitBranch, MessageSquare } from 'lucide-react';
import { buildRuleFlow } from './automationFlowLayout';
import { automationFlowNodeTypes, type FlowSharedActions, type FlowConditionActions } from './AutomationFlowNodes';
import type { CategoryOption } from './CategoryAutocompleteInput';
import {
  updateNode,
  removeNode,
  insertAfter,
  convertToBranch,
  appendToCase,
  addCase,
  removeCase,
  updateCase,
  updateBranchField,
  makeActionNode,
  type ActionNode,
} from './automationActionTree';

// Component palette (2026-08-26) — "right side panel, components you can
// add to the chain" per stakeholder feedback, replacing the old flow's only
// way to add a step (a generic "+ Action" ghost node defaulting to Assign to
// Agent, then hand-picking a type from its dropdown). Clicking a palette
// item appends that specific action type to the END of the top-level chain
// directly — same underlying `onChange([...actions, node])` the ghost node
// already used, just parameterized by type instead of always defaulting to
// assign_agent. Deliberately a fixed palette of the most-used types, not
// every ACTION_TYPES entry — the type dropdown on any resulting node still
// covers the full list for less-common ones.
const PALETTE_ITEMS: Array<{ type: string; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { type: 'wait', label: 'Wait', icon: Clock },
  { type: 'notify_agent', label: 'Notify Agent', icon: Bell },
  { type: 'notify_manager', label: 'Notify Manager', icon: Bell },
  { type: 'assign_agent', label: 'Assign Agent', icon: UserPlus },
  { type: 'assign_team', label: 'Assign Team', icon: Users },
  { type: 'send_message', label: 'Send Message', icon: MessageSquare },
  { type: 'branch', label: 'Branch', icon: GitBranch },
];

interface Props {
  triggerEvent: string;
  conditions: any[];
  onConditionsChange: (next: any[]) => void;
  actions: ActionNode[];
  onChange: (next: ActionNode[]) => void;
  isTicketEvent: boolean;
  agents: any[];
  teams: any[];
  labels: any[];
  categories: CategoryOption[];
  templates: Array<{ id: number; name: string; language?: string }>;
  channels: Array<{ id: number; name: string }>;
}

const DEFAULT_CONDITION = { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' };

/** Recursively counts real (non-empty) actions under a subtree — used only
 * to decide whether deleting a branch needs a confirm prompt. */
function countDescendantActions(node: ActionNode): number {
  if (node.action_type !== 'branch' || !node.action_parameters) return 0;
  const params = node.action_parameters;
  let count = 0;
  for (const c of params.cases || []) count += (c.actions || []).length;
  count += (params.default_actions || []).length;
  return count;
}

function findNode(list: ActionNode[], id: string): ActionNode | null {
  for (const n of list) {
    if (n._id === id) return n;
    if (n.action_type === 'branch' && n.action_parameters) {
      for (const c of n.action_parameters.cases || []) {
        const found = findNode(c.actions || [], id);
        if (found) return found;
      }
      const found = findNode(n.action_parameters.default_actions || [], id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Node-graph view of a rule's action tree ("join the nodes" — 2026-08-08).
 * Renders the same `actions` array the flat form edits, as a real diamond-
 * shaped flow (branches fan out per case and fan back in on whatever
 * follows, matching automation_engine.execute_action_list's actual runtime
 * order). Nodes are 100% derived from `actions` on every render — dragging
 * is disabled and layout is recomputed automatically, so there's no
 * separate position state to keep in sync.
 */
const AutomationFlowEditor: React.FC<Props> = ({
  triggerEvent, conditions, onConditionsChange, actions, onChange, isTicketEvent, agents, teams, labels, categories,
  templates, channels,
}) => {
  const conditionActions: FlowConditionActions = useMemo(() => ({
    isTicketEvent, categories,
    onAddCondition: () => onConditionsChange([...conditions, { ...DEFAULT_CONDITION }]),
    onRemoveCondition: (index) => onConditionsChange(conditions.filter((_, i) => i !== index)),
    onChangeCondition: (index, field, value) => {
      const next = [...conditions];
      next[index] = { ...next[index], [field]: value };
      onConditionsChange(next);
    },
  }), [conditions, onConditionsChange, isTicketEvent, categories]);

  const shared: FlowSharedActions = useMemo(() => ({
    isTicketEvent, agents, teams, labels, categories, templates, channels,
    onChangeActionType: (id, actionType) => onChange(updateNode(actions, id, { action_type: actionType })),
    onChangeActionParam: (id, key, value) =>
      onChange(updateNode(actions, id, (n) => ({ action_parameters: { ...(n.action_parameters || {}), [key]: value } }))),
    onDeleteAction: (id, isBranch) => {
      if (isBranch) {
        const node = findNode(actions, id);
        const count = node ? countDescendantActions(node) : 0;
        if (count > 0 && !window.confirm(`Delete this branch and its ${count} nested action(s)?`)) return;
      }
      onChange(removeNode(actions, id));
    },
    onConvertToBranch: (id) => onChange(convertToBranch(actions, id)),
    onInsertActionAfter: (id) => onChange(insertAfter(actions, id, makeActionNode())),
    onChangeBranchField: (branchId, field) => onChange(updateBranchField(actions, branchId, { field })),
    onAddCase: (branchId) => onChange(addCase(actions, branchId)),
    onRemoveCase: (branchId, caseId) => onChange(removeCase(actions, branchId, caseId)),
    onUpdateCase: (branchId, caseId, patch) => onChange(updateCase(actions, branchId, caseId, patch)),
    onAddActionTo: (branchId, caseId) => {
      const node = makeActionNode();
      if (branchId === null) onChange([...actions, node]);
      else onChange(appendToCase(actions, branchId, caseId as string, node));
    },
  }), [actions, onChange, isTicketEvent, agents, teams, labels, categories, templates, channels]);

  const { nodes, edges } = useMemo(() => {
    const built = buildRuleFlow(triggerEvent, conditions, actions);
    const withData: Node[] = built.nodes.map((n) => ({
      ...n,
      data: { ...n.data, actions: shared, conditionsActions: conditionActions },
    }));
    return { nodes: withData, edges: built.edges };
  }, [triggerEvent, conditions, actions, shared, conditionActions]);

  // Appends a palette item to the end of the top-level chain. 'branch' needs
  // its required shape (field + at least one case) seeded — convertToBranch
  // already knows how to do that for an existing node, so a plain node is
  // appended first and immediately converted by its own fresh _id, rather
  // than duplicating that shape here.
  const handlePaletteAdd = (actionType: string) => {
    if (actionType === 'branch') {
      const node = makeActionNode();
      onChange(convertToBranch([...actions, node], node._id));
      return;
    }
    onChange([...actions, makeActionNode(actionType)]);
  };

  return (
    <div className="flex h-[560px] w-full gap-3">
      <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-muted/10">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={automationFlowNodeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnScroll
            zoomOnScroll={false}
            zoomOnPinch
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {/* Component palette — see PALETTE_ITEMS above. */}
      <div className="w-44 shrink-0 overflow-y-auto rounded-lg border border-border bg-card p-2.5">
        <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Add to chain
        </p>
        <div className="space-y-1.5">
          {PALETTE_ITEMS.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => handlePaletteAdd(type)}
              className="flex w-full items-center gap-2 rounded-md border border-border/60 bg-background px-2.5 py-2 text-left text-xs font-medium transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {label}
            </button>
          ))}
        </div>
        <p className="mt-3 px-1 text-[11px] leading-relaxed text-muted-foreground">
          Adds to the end of the chain below. Drag isn't supported yet — reorder by deleting and re-adding.
        </p>
      </div>
    </div>
  );
};

export default AutomationFlowEditor;
